import { useState, useEffect, useCallback } from 'react';
import { fetchSuggestionCount } from '../api/templates';
import { SUGGESTIONS_CHANGED_EVENT } from '../utils/agentTemplates';

const POLL_INTERVAL_MS = 60000;

/**
 * Number of pending template suggestions, for the sidebar badge. Polls once a minute (not while the
 * tab is hidden, and recounts as soon as it is shown again) and recounts immediately when a suggestion is dismissed or used. Failures keep the
 * last known count: a badge that flickers to zero on a network blip would be worse than a stale one.
 * Pass `enabled = false` for users who cannot see suggestions; nothing is requested then.
 */
const useTemplateSuggestionCount = (enabled) => {
	const [count, setCount] = useState(0);

	const refresh = useCallback(async () => {
		if (document.hidden) return;
		try {
			setCount(await fetchSuggestionCount());
		} catch {
			// keep the previous count
		}
	}, []);

	useEffect(() => {
		if (!enabled) {
			setCount(0);
			return undefined;
		}
		refresh();
		const timer = setInterval(refresh, POLL_INTERVAL_MS);
		window.addEventListener(SUGGESTIONS_CHANGED_EVENT, refresh);
		document.addEventListener('visibilitychange', refresh);
		return () => {
			clearInterval(timer);
			window.removeEventListener(SUGGESTIONS_CHANGED_EVENT, refresh);
			document.removeEventListener('visibilitychange', refresh);
		};
	}, [enabled, refresh]);

	return count;
};

export default useTemplateSuggestionCount;
